// NEM12 Interval Export: interval day-rows (stored bv_nem_interval rows, or a
// parsed NEM12 file) → one CSV/XLSX row per interval, a monthly demand profile
// and an average-day curve per month.
//
// Channels follow NEM12 suffix letters: E = import kWh, B = export kWh,
// Q = import kVArh, K = export kVArh. kW = kWh × 60 / interval length. kVA is
// only derived where an E and a Q reading exist for the same interval:
// kVA = √(kWh² + kVArh²) × 60 / interval length. Otherwise it stays blank.
//
// Dates are `YYYY-MM-DD` strings handled in UTC so no time zone moves a day.

export type IntervalEvent = { startInterval: number; endInterval: number; qualityMethod: string };

/** One NEM12 `300` record: a channel's readings for one day. */
export type IntervalDayRow = {
  nmi: string;
  suffix: string;                  // E1, B1, Q1, K1…
  date: string;                    // YYYY-MM-DD
  intervalMin: number;             // 5 | 15 | 30
  values: Array<number | null>;    // position = slot (0-based); null / NaN = no reading
  quality: string;                 // day-level flag A/E/F/S/V/N
  uom?: string;
  events?: IntervalEvent[];        // NEM12 400 records (1-based, inclusive)
  provider?: string;
  updatedAt?: string;              // tie-break when two providers hold the same day
};

/** A bv_nem_interval row as Supabase returns it. */
export type StoredIntervalRow = {
  nmi: string;
  nmi_suffix: string;
  interval_date: string;
  interval_min: number;
  values: Array<number | string | null> | null;
  quality_flag: string | null;
  quality_events: IntervalEvent[] | null;
  uom: string | null;
  provider: string | null;
  updated_at: string | null;
};

export const MAX_RANGE_MONTHS = 13;
/** Excel's row limit, less the header row. */
export const XLSX_MAX_ROWS = 1_048_575;

const round = (n: number, dp: number) => Math.round(n * 10 ** dp) / 10 ** dp;
const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const kind = (suffix: string) => suffix.trim().toUpperCase().charAt(0);

export function fromStoredRow(r: StoredIntervalRow): IntervalDayRow {
  return {
    nmi: r.nmi,
    suffix: r.nmi_suffix,
    date: r.interval_date,
    intervalMin: r.interval_min,
    values: (r.values ?? []).map((v) => (v == null || v === '' ? null : Number(v))),
    quality: (r.quality_flag ?? 'A').trim().toUpperCase() || 'A',
    uom: r.uom ?? undefined,
    events: r.quality_events ?? undefined,
    provider: r.provider ?? undefined,
    updatedAt: r.updated_at ?? undefined,
  };
}

/** kWh in one interval → average kW over it. */
export function toKw(kwh: number, intervalMin: number): number {
  return (kwh * 60) / intervalMin;
}

/** kWh + kVArh in one interval → average kVA over it. */
export function toKva(kwh: number, kvarh: number, intervalMin: number): number {
  return (Math.sqrt(kwh * kwh + kvarh * kvarh) * 60) / intervalMin;
}

/** Minutes after midnight → 'HH:MM'. */
export function minuteLabel(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}

/** Every date from `from` to `to`, inclusive. */
export function daysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  const d = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  for (; d <= end; d.setUTCDate(d.getUTCDate() + 1)) out.push(d.toISOString().slice(0, 10));
  return out;
}

/** True when `to` is more than 13 months after `from` (or before it). */
export function rangeInvalid(from: string, to: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return 'Pick a start and end date.';
  if (from > to) return 'The start date is after the end date.';
  const limit = new Date(`${from}T00:00:00Z`);
  limit.setUTCMonth(limit.getUTCMonth() + MAX_RANGE_MONTHS);
  if (new Date(`${to}T00:00:00Z`) >= limit) return `Pick at most ${MAX_RANGE_MONTHS} months.`;
  return null;
}

/** Dates as ranges: ['2026-01-03','2026-01-04','2026-01-05','2026-01-09'] → '2026-01-03 to 2026-01-05, 2026-01-09'. */
export function dateRanges(dates: string[]): string {
  const sorted = [...dates].sort();
  const parts: string[] = [];
  let start = '';
  let prev = '';
  const next = (d: string) => {
    const x = new Date(`${d}T00:00:00Z`);
    x.setUTCDate(x.getUTCDate() + 1);
    return x.toISOString().slice(0, 10);
  };
  const flush = () => { if (start) parts.push(start === prev ? start : `${start} to ${prev}`); };
  for (const d of sorted) {
    if (start && d === next(prev)) { prev = d; continue; }
    flush();
    start = d;
    prev = d;
  }
  flush();
  return parts.join(', ');
}

export type DedupeResult = { days: IntervalDayRow[]; duplicates: number };

/**
 * One row per (NMI, channel, date). Two providers can hold the same day; the
 * later-updated one wins (else the later in input order). Output is sorted by
 * date, then channel.
 */
export function dedupeDays(rows: IntervalDayRow[]): DedupeResult {
  const byKey = new Map<string, IntervalDayRow>();
  let duplicates = 0;
  for (const r of rows) {
    const key = `${r.nmi}|${r.suffix}|${r.date}`;
    const prev = byKey.get(key);
    if (prev) {
      duplicates++;
      if (prev.updatedAt && r.updatedAt && prev.updatedAt > r.updatedAt) continue;
    }
    byKey.set(key, r);
  }
  const days = [...byKey.values()].sort((a, b) =>
    a.date === b.date ? a.suffix.localeCompare(b.suffix) : a.date < b.date ? -1 : 1);
  return { days, duplicates };
}

/** Quality of one slot: a 400 event covering it, else the day flag. */
function slotQuality(day: IntervalDayRow, slot: number): string {
  const ev = day.events?.find((e) => slot + 1 >= e.startInterval && slot + 1 <= e.endInterval);
  return (ev?.qualityMethod?.charAt(0) || day.quality || 'A').toUpperCase();
}

/** Sorted channel suffixes: E, B, Q, K, then anything else. */
export function channelOrder(suffixes: Iterable<string>): string[] {
  const rank = (s: string) => {
    const i = 'EBQK'.indexOf(kind(s));
    return i < 0 ? 9 : i;
  };
  return [...new Set(suffixes)].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

const unitFor = (suffix: string) => (kind(suffix) === 'Q' || kind(suffix) === 'K' ? 'kVArh' : 'kWh');

/** One interval: the per-channel readings at a date + start minute. */
type Slot = {
  date: string;
  startMin: number;
  len: number;                       // the import channel's length, else the first channel's
  byChannel: Map<string, number>;    // suffix → reading
  quality: Set<string>;
  importKwh: number | null;          // sum of E channels
  exportKwh: number | null;          // sum of B channels
  importKvarh: number | null;        // sum of Q channels
  importLen: number | null;
  kvarhLen: number | null;
};

/** Walk every interval in date + time order. */
function* slots(days: IntervalDayRow[]): Generator<Slot> {
  const byDate = new Map<string, IntervalDayRow[]>();
  for (const d of days) {
    const list = byDate.get(d.date) ?? [];
    list.push(d);
    byDate.set(d.date, list);
  }
  for (const date of [...byDate.keys()].sort()) {
    const grid = new Map<number, Slot>();
    for (const day of byDate.get(date) ?? []) {
      const len = day.intervalMin || Math.round(1440 / (day.values.length || 288));
      const k = kind(day.suffix);
      day.values.forEach((raw, i) => {
        const startMin = i * len;
        let s = grid.get(startMin);
        if (!s) {
          s = { date, startMin, len, byChannel: new Map(), quality: new Set(), importKwh: null, exportKwh: null, importKvarh: null, importLen: null, kvarhLen: null };
          grid.set(startMin, s);
        }
        if (!finite(raw)) return;
        s.byChannel.set(day.suffix, raw);
        s.quality.add(slotQuality(day, i));
        if (k === 'E') { s.importKwh = (s.importKwh ?? 0) + raw; s.importLen = len; s.len = len; }
        else if (k === 'B') s.exportKwh = (s.exportKwh ?? 0) + raw;
        else if (k === 'Q') { s.importKvarh = (s.importKvarh ?? 0) + raw; s.kvarhLen = len; }
      });
    }
    for (const startMin of [...grid.keys()].sort((a, b) => a - b)) yield grid.get(startMin) as Slot;
  }
}

function slotKw(s: Slot): number | null {
  return s.importKwh == null || s.importLen == null ? null : toKw(s.importKwh, s.importLen);
}

function slotKva(s: Slot): number | null {
  if (s.importKwh == null || s.importKvarh == null || s.importLen == null || s.kvarhLen !== s.importLen) return null;
  return toKva(s.importKwh, s.importKvarh, s.importLen);
}

export type IntervalRowsResult = { rows: Record<string, unknown>[]; total: number; truncated: boolean };

/**
 * One row per interval. Columns: NMI, Date, Start, End, a reading column per
 * channel (`E1 kWh`, `B1 kWh`, `Q1 kVArh`…), Import kW, kVA (blank without a
 * Q channel), Quality (every flag seen in that interval, e.g. `A` or `A/S`).
 * `limit` caps the rows kept (Excel's limit for XLSX); `total` counts all.
 */
export function intervalRows(days: IntervalDayRow[], limit = Infinity): IntervalRowsResult {
  const channels = channelOrder(days.map((d) => d.suffix));
  const nmi = days[0]?.nmi ?? '';
  const rows: Record<string, unknown>[] = [];
  let total = 0;
  for (const s of slots(days)) {
    total++;
    if (rows.length >= limit) continue;
    const row: Record<string, unknown> = {
      NMI: nmi,
      Date: s.date,
      Start: minuteLabel(s.startMin),
      End: minuteLabel(Math.min(s.startMin + s.len, 1440)).replace('24:00', '00:00'),
    };
    for (const c of channels) {
      const v = s.byChannel.get(c);
      row[`${c} ${unitFor(c)}`] = v == null ? '' : round(v, 4);
    }
    const kw = slotKw(s);
    const kva = slotKva(s);
    row['Import kW'] = kw == null ? '' : round(kw, 3);
    row.kVA = kva == null ? '' : round(kva, 3);
    row.Quality = [...s.quality].sort().join('/');
    rows.push(row);
  }
  return { rows, total, truncated: total > rows.length };
}

/** Interval rows as CSV text (RFC 4180 quoting). */
export function toCsv(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return '';
  const cell = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const headers = Object.keys(rows[0]);
  return [headers.map(cell).join(','), ...rows.map((r) => headers.map((h) => cell(r[h])).join(','))].join('\n');
}

export type MonthDemand = {
  month: string;              // YYYY-MM
  daysWithData: number;
  importKwh: number;
  exportKwh: number;
  peakKw: number | null;
  peakKwDate: string;
  peakKwTime: string;
  peakKva: number | null;
  peakKvaDate: string;
  peakKvaTime: string;
  /** Average kW over the days with data ÷ peak kW. */
  loadFactor: number | null;
};

/**
 * Per month: total import/export kWh, the peak kW and peak kVA interval (date
 * and start time), and load factor. Peaks are at the data's own interval
 * length (a 5-minute file peaks on a 5-minute interval). Load factor uses the
 * hours of the days that have data, so missing days do not drag it down.
 */
export function monthlyDemand(days: IntervalDayRow[]): MonthDemand[] {
  const months = new Map<string, MonthDemand & { dates: Set<string> }>();
  for (const s of slots(days)) {
    const month = s.date.slice(0, 7);
    let m = months.get(month);
    if (!m) {
      m = { month, daysWithData: 0, importKwh: 0, exportKwh: 0, peakKw: null, peakKwDate: '', peakKwTime: '', peakKva: null, peakKvaDate: '', peakKvaTime: '', loadFactor: null, dates: new Set() };
      months.set(month, m);
    }
    if (s.byChannel.size > 0) m.dates.add(s.date);
    m.importKwh += s.importKwh ?? 0;
    m.exportKwh += s.exportKwh ?? 0;
    const kw = slotKw(s);
    if (kw != null && (m.peakKw == null || kw > m.peakKw)) {
      m.peakKw = kw; m.peakKwDate = s.date; m.peakKwTime = minuteLabel(s.startMin);
    }
    const kva = slotKva(s);
    if (kva != null && (m.peakKva == null || kva > m.peakKva)) {
      m.peakKva = kva; m.peakKvaDate = s.date; m.peakKvaTime = minuteLabel(s.startMin);
    }
  }
  return [...months.values()].sort((a, b) => (a.month < b.month ? -1 : 1)).map(({ dates, ...m }) => {
    const hours = dates.size * 24;
    const avgKw = hours > 0 ? m.importKwh / hours : 0;
    return {
      ...m,
      daysWithData: dates.size,
      importKwh: round(m.importKwh, 3),
      exportKwh: round(m.exportKwh, 3),
      peakKw: m.peakKw == null ? null : round(m.peakKw, 3),
      peakKva: m.peakKva == null ? null : round(m.peakKva, 3),
      loadFactor: m.peakKw ? round(avgKw / m.peakKw, 3) : null,
    };
  });
}

export type AverageDay = {
  months: string[];                         // YYYY-MM, ascending
  /** 48 rows: half-hour start label + average import kW per month (null = no data). */
  rows: Array<{ time: string; kw: Array<number | null> }>;
};

/**
 * Average-day 30-minute import curve per month. Each half-hour's kW is its
 * import kWh × 2 (5- and 15-minute readings summed into the half-hour first),
 * averaged over the days with data in that half-hour.
 */
export function averageDay(days: IntervalDayRow[]): AverageDay {
  // month → 48 × { sum kW, days }
  const acc = new Map<string, Array<{ sum: number; n: number }>>();
  // date → 48 half-hour kWh sums (import only)
  const perDate = new Map<string, Array<number | null>>();
  for (const s of slots(days)) {
    if (s.importKwh == null) continue;
    const hh = Math.floor(s.startMin / 30);
    const arr = perDate.get(s.date) ?? new Array<number | null>(48).fill(null);
    arr[hh] = (arr[hh] ?? 0) + s.importKwh;
    perDate.set(s.date, arr);
  }
  for (const [date, halfHours] of perDate) {
    const month = date.slice(0, 7);
    const m = acc.get(month) ?? Array.from({ length: 48 }, () => ({ sum: 0, n: 0 }));
    halfHours.forEach((kwh, i) => { if (kwh != null) { m[i].sum += kwh * 2; m[i].n++; } });
    acc.set(month, m);
  }
  const months = [...acc.keys()].sort();
  const rows = Array.from({ length: 48 }, (_, i) => ({
    time: minuteLabel(i * 30),
    kw: months.map((mo) => {
      const c = (acc.get(mo) as Array<{ sum: number; n: number }>)[i];
      return c.n > 0 ? round(c.sum / c.n, 3) : null;
    }),
  }));
  return { months, rows };
}

export type ExportSummary = {
  nmi: string;
  source: string;
  periodFrom: string;
  periodTo: string;
  channels: string[];
  intervalLengths: number[];
  daysExpected: number;
  daysWithData: number;
  missingDays: string[];
  /** Day-level quality flag → channel-days. */
  qualityDays: Record<string, number>;
  intervals: number;
  importKwh: number;
  exportKwh: number;
  duplicateDays: number;
};

/** Headline figures. Expected days run from `from` to `to` (inclusive). */
export function exportSummary(
  days: IntervalDayRow[],
  opts: { nmi: string; source: string; from: string; to: string; duplicates?: number },
): ExportSummary {
  const dates = new Set<string>();
  const qualityDays: Record<string, number> = {};
  const lens = new Set<number>();
  let importKwh = 0;
  let exportKwh = 0;
  let intervals = 0;
  for (const d of days) {
    if (d.values.some(finite)) dates.add(d.date);
    qualityDays[d.quality] = (qualityDays[d.quality] ?? 0) + 1;
    lens.add(d.intervalMin);
    const k = kind(d.suffix);
    for (const v of d.values) {
      if (!finite(v)) continue;
      if (k === 'E') importKwh += v;
      else if (k === 'B') exportKwh += v;
    }
  }
  for (const s of slots(days)) if (s.byChannel.size > 0) intervals++;
  const expected = opts.from && opts.to ? daysBetween(opts.from, opts.to) : [];
  return {
    nmi: opts.nmi,
    source: opts.source,
    periodFrom: opts.from,
    periodTo: opts.to,
    channels: channelOrder(days.map((d) => d.suffix)),
    intervalLengths: [...lens].sort((a, b) => a - b),
    daysExpected: expected.length,
    daysWithData: dates.size,
    missingDays: expected.filter((d) => !dates.has(d)),
    qualityDays,
    intervals,
    importKwh: round(importKwh, 3),
    exportKwh: round(exportKwh, 3),
    duplicateDays: opts.duplicates ?? 0,
  };
}

const QUALITY_NAMES: Record<string, string> = {
  A: 'Actual', E: 'Estimated', F: 'Final substituted', S: 'Substituted', V: 'Variable', N: 'Null',
};

/** Summary sheet rows (Item / Value). */
export function summarySheetRows(s: ExportSummary, intervalNote = ''): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = [
    { Item: 'NMI', Value: s.nmi },
    { Item: 'Source', Value: s.source },
    { Item: 'Period from', Value: s.periodFrom },
    { Item: 'Period to', Value: s.periodTo },
    { Item: 'Channels', Value: s.channels.join(', ') },
    { Item: 'Interval length (min)', Value: s.intervalLengths.join(', ') },
    { Item: 'Days in period', Value: s.daysExpected },
    { Item: 'Days with data', Value: s.daysWithData },
    { Item: 'Missing days', Value: s.missingDays.length },
    { Item: 'Missing dates', Value: dateRanges(s.missingDays) },
    { Item: 'Intervals', Value: s.intervals },
    { Item: 'Import kWh', Value: s.importKwh },
    { Item: 'Export kWh', Value: s.exportKwh },
  ];
  for (const [flag, n] of Object.entries(s.qualityDays).sort()) {
    rows.push({ Item: `Channel-days quality ${flag} (${QUALITY_NAMES[flag] ?? flag})`, Value: n });
  }
  if (s.duplicateDays > 0) {
    rows.push({ Item: 'Channel-days held by two providers (latest kept)', Value: s.duplicateDays });
  }
  if (intervalNote) rows.push({ Item: 'Note', Value: intervalNote });
  return rows;
}

/** Demand profile sheet rows. */
export function demandSheetRows(months: MonthDemand[]): Record<string, unknown>[] {
  return months.map((m) => ({
    Month: m.month,
    'Days with data': m.daysWithData,
    'Import kWh': m.importKwh,
    'Export kWh': m.exportKwh,
    'Peak kW': m.peakKw ?? '',
    'Peak kW date': m.peakKwDate,
    'Peak kW time': m.peakKwTime,
    'Peak kVA': m.peakKva ?? '',
    'Peak kVA date': m.peakKvaDate,
    'Peak kVA time': m.peakKvaTime,
    'Load factor': m.loadFactor ?? '',
  }));
}

/** Average-day sheet rows: Time + one kW column per month. */
export function averageDaySheetRows(avg: AverageDay): Record<string, unknown>[] {
  return avg.rows.map((r) => {
    const row: Record<string, unknown> = { Time: r.time };
    avg.months.forEach((m, i) => { row[`${m} kW`] = r.kw[i] ?? ''; });
    return row;
  });
}
